import { inject, type App, type InjectionKey } from 'vue';

export type PushState =
  /** This browser can't do web push (or isn't allowed to here: iPhone outside the home screen). */
  | 'unsupported'
  /** The person blocked notifications for Dometry in the browser. */
  | 'blocked'
  | 'off'
  | 'on';

export interface DeviceSubscription {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  label: string;
}

/** What the app needs from the browser's push machinery (tests provide a fake). */
export interface Push {
  state(): Promise<PushState>;
  /** Ask permission (only ever from a click) and subscribe this browser. */
  subscribe(publicKey: string): Promise<DeviceSubscription>;
  /** Stop push in this browser. */
  unsubscribe(): Promise<void>;
  /** A hash of this browser's endpoint, to recognize it in the device list. */
  currentHash(): Promise<string | null>;
  install(app: App): void;
}

const SW = '/sw.js';

/** The service worker, registered once (it only handles push and notification clicks). */
export function registerServiceWorker(): void {
  if ('serviceWorker' in navigator)
    void navigator.serviceWorker.register(SW).catch(() => undefined);
}

function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = value
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(value.length / 4) * 4, '=');
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/** "Chrome on Windows", for the device list. */
export function deviceLabel(ua: string = navigator.userAgent): string {
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Firefox\//.test(ua)
      ? 'Firefox'
      : /Chrome\//.test(ua)
        ? 'Chrome'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'Browser';
  const os = /iPhone|iPad/.test(ua)
    ? 'iPhone'
    : /Android/.test(ua)
      ? 'Android'
      : /Windows/.test(ua)
        ? 'Windows'
        : /Mac OS X/.test(ua)
          ? 'Mac'
          : /Linux/.test(ua)
            ? 'Linux'
            : 'this device';
  return `${browser} on ${os}`;
}

/** Web push through the service worker and the browser's PushManager. */
export class PushClient implements Push {
  async state(): Promise<PushState> {
    if (
      !('serviceWorker' in navigator) ||
      !('PushManager' in window) ||
      !('Notification' in window)
    )
      return 'unsupported';
    if (Notification.permission === 'denied') return 'blocked';
    const registration = await navigator.serviceWorker.getRegistration(SW);
    const subscription = await registration?.pushManager.getSubscription();
    return subscription && Notification.permission === 'granted' ? 'on' : 'off';
  }

  async subscribe(publicKey: string): Promise<DeviceSubscription> {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted')
      throw new Error(
        permission === 'denied'
          ? 'Notifications are blocked for Dometry in this browser.'
          : 'Allow notifications to turn push on.',
      );
    const registration = await navigator.serviceWorker.register(SW);
    await navigator.serviceWorker.ready;
    const existing = await registration.pushManager.getSubscription();
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: base64UrlToBytes(publicKey),
      }));
    const json = subscription.toJSON() as { endpoint?: string; keys?: Record<string, string> };
    return {
      endpoint: json.endpoint ?? subscription.endpoint,
      keys: { p256dh: json.keys?.['p256dh'] ?? '', auth: json.keys?.['auth'] ?? '' },
      label: deviceLabel(),
    };
  }

  async unsubscribe(): Promise<void> {
    const registration = await navigator.serviceWorker.getRegistration(SW);
    await (await registration?.pushManager.getSubscription())?.unsubscribe();
  }

  async currentHash(): Promise<string | null> {
    if (!('serviceWorker' in navigator)) return null;
    const registration = await navigator.serviceWorker.getRegistration(SW);
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return null;
    const digest = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(subscription.endpoint),
    );
    return [...new Uint8Array(digest)]
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
      .slice(0, 32);
  }

  install(app: App): void {
    app.provide(PUSH_KEY, this);
  }
}

export const PUSH_KEY: InjectionKey<Push> = Symbol('dometry-push');

export function usePush(): Push {
  const push = inject(PUSH_KEY, null);
  if (!push) throw new Error('usePush() called without the push plugin installed');
  return push;
}
